import '../Home.css';
import { allCountries } from 'country-telephone-data';
import { useState, useRef, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleXmark, faTimes, faExclamationTriangle, faCircleUser, faGear, faRightFromBracket, faAngleDown, faAngleUp, faCircleQuestion, faChevronRight } from "@fortawesome/free-solid-svg-icons";
import { useNavigate } from 'react-router-dom';

export default function MyAccountCard({ isLoggedIn, name, onExpandChange, types, onLoginSuccess, setUser, setAccountError }) {
  const [isOpen, setIsOpen] = useState(false);
  const formRef = useRef(null);
  const cardRef = useRef(null);
  const [formHeight, setFormHeight] = useState(0);
  const animatingRef = useRef(false);
  const [displayName, setDisplayName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [isValid, setIsValid] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const emailOk = types?.["std.email"] && email.trim() ? types["std.email"].input_is_ok(email)[0] : false;
    const phoneOk = types?.["std.tel"] && phone.trim() ? types["std.tel"].input_is_ok(phone)[0] : false;
    const valid = displayName.trim().length > 0 && (emailOk || phoneOk);
    setIsValid(valid);
  }, [displayName, email, phone, types]);
  

  useEffect(() => {
    if (formRef.current) {
      setFormHeight(isOpen ? formRef.current.scrollHeight : 0);
    }
  }, [isOpen]);

  const filteredCountries = allCountries.filter(c => Number(c.dialCode) <= 999);
  const priorityCountries = ["us", "gb", "ca", "au"];
  const top = filteredCountries.filter(c => priorityCountries.includes(c.iso2));
  const rest = filteredCountries.filter(c => !priorityCountries.includes(c.iso2));

  const handleClick = () => {
    if (animatingRef.current) return;
    animatingRef.current = true;
    const grid = cardRef.current?.closest('.card-grid');

    if (grid) {
      grid.classList.add('cards-leaving');
      setTimeout(() => {
        grid.classList.remove('cards-leaving');
        setIsOpen(prev => { const next = !prev; onExpandChange?.(next); return next; });
        requestAnimationFrame(() => {
          grid.classList.add('cards-entering');
          setTimeout(() => { grid.classList.remove('cards-entering'); animatingRef.current = false; }, 300);
        });
      }, 170);
    } else {
      setIsOpen(prev => { const next = !prev; onExpandChange?.(next); return next; });
      animatingRef.current = false;
    }
  };

  const isEmailValid = () => {
    if (!types?.["std.email"] || !email.trim()) return false;
    const [valid] = types["std.email"].input_is_ok(email);
    return valid;
  };

  const isPhoneValid = () => {
    if (!types?.["std.tel"] || !phone.trim()) return false;
    const [valid] = types["std.tel"].input_is_ok(phone);
    return valid;
  };

  const handleTempAccountCreate = async () => {
    if (!isValid) return;

    const emailValid = isEmailValid();
    const phoneValid = isPhoneValid();
    const selectEl = cardRef.current?.querySelector(".temp-phone-select");
    const selectedCountryCode = selectEl?.value || "1";

    const payload = {
      account_type: "CONTACT_ONLY",
      enabled_routes: ["NO_CHECK"],
      display_name: displayName.trim()
    };
    if (emailValid) payload.email = email.trim();
    if (phoneValid) { payload.tel_country_code = selectedCountryCode; payload.tel_number = phone.trim(); }

    try {
      const createForm = new FormData();
      createForm.append("payload", JSON.stringify(payload));

      const createRes = await fetch(
        "https://access-control-api.fishtagged.com/account/create",
        { method: "POST", body: createForm, credentials: "include" }
      );
      const createData = await createRes.json();
      if (createData.status !== 200) {
        const err = createData.errors?.[0];
        const msg = typeof err === 'string'
          ? err
          : err?.explanation || '';

        // 🔹 If account already exists
        if (msg.toLowerCase().includes('already')) {

          // Try logging in as CONTACT_ONLY
          const loginSuccess = await attemptLogin();

          if (loginSuccess) {
            setIsOpen(false);
            return;
          }

          // If login fails → assume FULL account exists
          setAccountError('Could not log in. This could be because of an invalid email/phone or because a full (non-temporary) account already exists with this email/phone.');
          return;
        }

        setAccountError(msg || 'Could not create account.');
        return;
      }

      const loginPayload = { authentication_route: "NO_CHECK" };
      if (phoneValid) { loginPayload.tel_country_code = selectedCountryCode; loginPayload.tel_number = phone.trim(); }
      else if (emailValid) loginPayload.email = email.trim();

      const loginForm = new FormData();
      loginForm.append("payload", JSON.stringify(loginPayload));

      const loginRes = await fetch(
        "https://access-control-api.fishtagged.com/session/initiate",
        { method: "POST", body: loginForm, credentials: "include", headers: { "x-development-cookie-protections": "DISABLE" } }
      );

      const loginData = await loginRes.json();
      console.log(loginData);

      if (loginData.status === 200) {
        console.log("Temp account created and logged in.");
        setAccountError('');
        onLoginSuccess?.({
          name: loginData.return_payload?.user?.display_name || displayName,
          accountType: "CONTACT_ONLY",
          isLoggedIn: true,
        });
        setIsOpen(false);
      } else {
        console.error(loginData.errors);
      }

    } catch (err) {
      console.error("Temp account error:", err);
    }
  };

  const attemptLogin = async () => {
    const emailValid = isEmailValid();
    const phoneValid = isPhoneValid();
    const selectEl = cardRef.current?.querySelector(".temp-phone-select");
    const selectedCountryCode = selectEl?.value || "1";

    const loginPayload = { authentication_route: "NO_CHECK" };

    if (phoneValid) {
      loginPayload.tel_country_code = selectedCountryCode;
      loginPayload.tel_number = phone.trim();
    } else if (emailValid) {
      loginPayload.email = email.trim();
    }

    const loginForm = new FormData();
    loginForm.append("payload", JSON.stringify(loginPayload));

    const loginRes = await fetch(
      "https://access-control-api.fishtagged.com/session/initiate",
      {
        method: "POST",
        body: loginForm,
        credentials: "include",
        headers: { "x-development-cookie-protections": "DISABLE" }
      }
    );

    const loginData = await loginRes.json();

    if (loginData.status === 200) {
      setAccountError('');
      onLoginSuccess?.({
        display_name: displayName,
        account_type: "CONTACT_ONLY",
      });
      setIsOpen(false);
      return true;
    }

    return false;
  };

  const handleSignOut = async () => {
    try {
      const form = new FormData();
      form.append("payload", JSON.stringify({ session_id: null }));
      await fetch('https://access-control-api.fishtagged.com/session/terminate', {
        method: 'POST',
        credentials: 'include',
        body: form,
      });
    } catch (err) {
      console.error('Sign out failed', err);
    }
    localStorage.removeItem("ft_user");
    setUser({ isLoggedIn: false, name: '', accountType: null });
  };

 return (
  <>
    <div ref={cardRef} className={`card${isOpen ? ' card-expanded' : ''}`}>
      <h2>
        My Account
        <div className="tooltip-wrap">
          <u className="help-icon">What is a temporary acct.?</u>
          <div className="tooltip">
            A temporary account is a quick-to-create, no-password account that still uniquely identifies you. You can convert it to a normal account later. Temporary accounts are still eligible for FishTagged Rewards.
          </div>
        </div>
      </h2>

      <div className="account-status-row">
        <FontAwesomeIcon
          icon={isLoggedIn ? faCircleUser : faCircleXmark}
          className="account-icon"
        />
        <span className={`chip ${isLoggedIn ? 'logged-in' : 'logged-out'}`}>
          {isLoggedIn ? `Logged in as ${name}` : 'Not Logged In'}
        </span>
      </div>

      <div className="account-status-row2">
        {isLoggedIn ? (
          <>
            <button className="switch-btn" onClick={() => navigate('/login')}>
              <FontAwesomeIcon icon={faGear} className="gear-icon" />
              Switch Account
            </button>
            <button className="signout-btn" onClick={handleSignOut}>
              <FontAwesomeIcon icon={faRightFromBracket} className="signout-icon" />
              Sign Out
            </button>
          </>
        ) : (
          <>
            <button className="login-signup-btn" onClick={() => navigate('/login')}>
              <FontAwesomeIcon icon={faGear} className="gear-icon" />
              Sign In/Create Account
            </button>
            <button className="temp-btn" onClick={handleClick}>
              Use Temporary Account
              <FontAwesomeIcon
                icon={isOpen ? faAngleUp : faAngleDown}
                className="temp-icon"
              />
            </button>
          </>
        )}
      </div>

      <div
        className="temp-form-slider"
        style={{ height: isOpen ? formHeight : 0 }}
      >
        <div ref={formRef} className="temp-form-inner">
          <input
            type="text"
            className="temp-input"
            placeholder="Your name"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
          />

          <input
            type="email"
            className="temp-input"
            placeholder="Your email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />

          <div className="temp-phone-row">
            <select className="temp-phone-select">
              {[...top, ...rest].map((c) => (
                <option key={c.iso2} value={c.dialCode}>
                  +{c.dialCode} ({c.iso2.toUpperCase()})
                </option>
              ))}
            </select>

            <input
              type="tel"
              className="temp-input temp-phone-input"
              placeholder="Phone number"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
            />
          </div>

          <button
            className={`temp-go-btn ${!isValid ? 'disabled' : ''}`}
            disabled={!isValid}
            onClick={handleTempAccountCreate}
          >
            Go <FontAwesomeIcon icon={faChevronRight} className="temp-go-arrow" />
          </button>
        </div>
      </div>
    </div>
  </>
);
}